import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import './ProfileSetupPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProfileSetupPage = () => {
  const navigate = useNavigate();
  const { user, sessionToken, refreshUser } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [profileData, setProfileData] = useState({
    gender: '',
    age: '',
    height: '',
    weight: '',
    shirt_size: '',
    pant_size: '',
    shoe_size: '',
    favorite_seasons: []
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSeasonToggle = (season) => {
    setProfileData(prev => {
      const seasons = prev.favorite_seasons || [];
      if (seasons.includes(season)) {
        return {
          ...prev,
          favorite_seasons: seasons.filter(s => s !== season)
        };
      } else {
        return {
          ...prev,
          favorite_seasons: [...seasons, season]
        };
      }
    });
  };

  const validateStep1 = () => {
    if (!profileData.gender || !profileData.age) {
      setError('Please select gender and enter your age');
      return false;
    }
    if (parseInt(profileData.age) < 1 || parseInt(profileData.age) > 120) {
      setError('Please enter a valid age');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    // Optional fields, just clear error
    setError('');
    return true;
  };

  const handleNext = () => {
    setError('');
    if (currentStep === 1 && !validateStep1()) {
      return;
    }
    if (currentStep === 2 && !validateStep2()) {
      return;
    }
    setCurrentStep(prev => prev + 1);
  };

  const handleBack = () => {
    setError('');
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    if (!validateStep1()) {
      setCurrentStep(1);
      return;
    }

    try {
      setLoading(true);
      setError('');

      const updateData = {
        gender: profileData.gender || null,
        age: profileData.age ? parseInt(profileData.age) : null,
        height: profileData.height || null,
        weight: profileData.weight || null,
        shirt_size: profileData.shirt_size || null,
        pant_size: profileData.pant_size || null,
        shoe_size: profileData.shoe_size || null,
        favorite_seasons: profileData.favorite_seasons
      };

      const response = await axios.put(
        `${API}/auth/profile`,
        updateData,
        {
          headers: {
            Authorization: `Bearer ${sessionToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('Profile update response:', response.data);

      // Refresh user data
      await refreshUser();

      // Wait a bit for the refresh to complete
      await new Promise(resolve => setTimeout(resolve, 500));

      // Redirect to model albums page to create first album
      alert('✅ Profile setup complete! \n\nNext step: Create your first Model Album to start using virtual try-on.');
      navigate('/models', { replace: true });

    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save profile');
    } finally {
      setLoading(false);
    }
  };

  const seasons = [
    { value: 'winter', label: 'Winter', emoji: '❄️' },
    { value: 'spring', label: 'Spring', emoji: '🌸' },
    { value: 'summer', label: 'Summer', emoji: '☀️' },
    { value: 'fall', label: 'Fall', emoji: '🍂' }
  ];

  return (
    <div className="setup-page">
      <div className="setup-container">
        <div className="setup-header">
          <div className="setup-logo">
            <span className="logo-icon">🎨</span>
            <span className="logo-text">FITTTY.AI</span>
          </div>
          <h1>Welcome, {user?.name}! 👋</h1>
          <p>Let&apos;s set up your profile to personalize your experience</p>
        </div>

        {/* Progress Bar */}
        <div className="progress-bar">
          <div className={`progress-step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="step-circle">1</div>
            <span className="step-label">Basic Info</span>
          </div>
          <div className={`progress-line ${currentStep > 1 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="step-circle">2</div>
            <span className="step-label">Measurements</span>
          </div>
          <div className={`progress-line ${currentStep > 2 ? 'completed' : ''}`}></div>
          <div className={`progress-step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-circle">3</div>
            <span className="step-label">Preferences</span>
          </div>
        </div>

        {error && <div className="setup-error">{error}</div>}

        <div className="setup-content">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="setup-step">
              <h2>📋 Basic Information</h2>
              <p className="step-description">Help us understand you better</p>

              <div className="form-group">
                <label>Gender <span className="required">*</span></label>
                <div className="gender-options">
                  <button
                    type="button"
                    className={`gender-btn ${profileData.gender === 'male' ? 'selected' : ''}`}
                    onClick={() => setProfileData(prev => ({ ...prev, gender: 'male' }))}
                  >
                    👨 Male
                  </button>
                  <button
                    type="button"
                    className={`gender-btn ${profileData.gender === 'female' ? 'selected' : ''}`}
                    onClick={() => setProfileData(prev => ({ ...prev, gender: 'female' }))}
                  >
                    👩 Female
                  </button>
                  <button
                    type="button"
                    className={`gender-btn ${profileData.gender === 'kids' ? 'selected' : ''}`}
                    onClick={() => setProfileData(prev => ({ ...prev, gender: 'kids' }))}
                  >
                    👶 Kids
                  </button>
                  <button
                    type="button"
                    className={`gender-btn ${profileData.gender === 'unisex' ? 'selected' : ''}`}
                    onClick={() => setProfileData(prev => ({ ...prev, gender: 'unisex' }))}
                  >
                    ⭐ Prefer not to say
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Age <span className="required">*</span></label>
                <input
                  type="number"
                  name="age"
                  value={profileData.age}
                  onChange={handleInputChange}
                  placeholder="Enter your age"
                  min="1"
                  max="120"
                  className="setup-input"
                />
              </div>
            </div>
          )}

          {/* Step 2: Measurements */}
          {currentStep === 2 && (
            <div className="setup-step">
              <h2>📏 Your Measurements</h2>
              <p className="step-description">Optional but helps us recommend better fits</p>

              <div className="form-row">
                <div className="form-group">
                  <label>Height</label>
                  <input
                    type="text"
                    name="height"
                    value={profileData.height}
                    onChange={handleInputChange}
                    placeholder="e.g., 5'10 or 178 cm"
                    className="setup-input"
                  />
                </div>

                <div className="form-group">
                  <label>Weight</label>
                  <input
                    type="text"
                    name="weight"
                    value={profileData.weight}
                    onChange={handleInputChange}
                    placeholder="e.g., 165 lbs or 75 kg"
                    className="setup-input"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Shirt Size (US)</label>
                  <select
                    name="shirt_size"
                    value={profileData.shirt_size}
                    onChange={handleInputChange}
                    className="setup-select"
                  >
                    <option value="">Select size</option>
                    <option value="XS">XS</option>
                    <option value="S">S</option>
                    <option value="M">M</option>
                    <option value="L">L</option>
                    <option value="XL">XL</option>
                    <option value="XXL">XXL</option>
                    <option value="XXXL">XXXL</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Pant Size (US)</label>
                  <input
                    type="text"
                    name="pant_size"
                    value={profileData.pant_size}
                    onChange={handleInputChange}
                    placeholder="e.g., 32x32, 34x30"
                    className="setup-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Shoe Size (US)</label>
                <input
                  type="text"
                  name="shoe_size"
                  value={profileData.shoe_size}
                  onChange={handleInputChange}
                  placeholder="e.g., 10, 10.5"
                  className="setup-input"
                  style={{ maxWidth: '200px' }}
                />
              </div>
            </div>
          )}

          {/* Step 3: Preferences */}
          {currentStep === 3 && (
            <div className="setup-step">
              <h2>🌟 Your Preferences</h2>
              <p className="step-description">Which seasons do you shop for most?</p>

              <div className="form-group">
                <label>Favorite Seasons</label>
                <div className="season-options">
                  {seasons.map(season => (
                    <button
                      key={season.value}
                      type="button"
                      className={`season-btn ${profileData.favorite_seasons?.includes(season.value) ? 'selected' : ''}`}
                      onClick={() => handleSeasonToggle(season.value)}
                    >
                      <span className="season-emoji">{season.emoji}</span>
                      <span className="season-label">{season.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="setup-summary">
                <h3>Profile Summary</h3>
                <div className="summary-grid">
                  <div className="summary-item">
                    <span className="summary-label">Gender:</span>
                    <span className="summary-value">{profileData.gender || 'Not set'}</span>
                  </div>
                  <div className="summary-item">
                    <span className="summary-label">Age:</span>
                    <span className="summary-value">{profileData.age || 'Not set'}</span>
                  </div>
                  {profileData.height && (
                    <div className="summary-item">
                      <span className="summary-label">Height:</span>
                      <span className="summary-value">{profileData.height}</span>
                    </div>
                  )}
                  {profileData.shirt_size && (
                    <div className="summary-item">
                      <span className="summary-label">Shirt Size:</span>
                      <span className="summary-value">{profileData.shirt_size}</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="setup-actions">
          {currentStep > 1 && (
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={loading}
            >
              ← Back
            </button>
          )}

          {currentStep < 3 ? (
            <button
              className="btn-primary"
              onClick={handleNext}
              disabled={loading}
            >
              Next →
            </button>
          ) : (
            <button
              className="btn-primary btn-finish"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? 'Saving...' : '🎉 Complete Setup'}
            </button>
          )}
        </div>

        {currentStep < 3 && (
          <button
            className="skip-btn"
            onClick={() => navigate('/tryon')}
          >
            Skip for now
          </button>
        )}
      </div>
    </div>
  );
};

export default ProfileSetupPage;
